import React, { useEffect, useState } from 'react'
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useWallet } from '../chain/WalletContext';
import { CHAIN, txUrl } from '../chain/config';
import '../components/css/Cart.css';

const unitPrice = (item) => parseFloat((item.productDetails || {}).price) || 0;

export default function Cart() {
	const { address, connect, payBRC, hasWallet } = useWallet();
	const [cartData, setCartData] = useState([]);
	const [payMethod, setPayMethod] = useState('BRC');
	const [addressForm, setAddressForm] = useState({ name: '', phone: '', street: '', city: '', notes: '' });
	const [phase, setPhase] = useState('cart'); // cart | placing | paying | verifying | done
	const [receipt, setReceipt] = useState(null); // { orderGroupId, amountBRC, totalUSD, txHash, method }
	const mytoken = localStorage.getItem('token');

	useEffect(() => {
		const stored = localStorage.getItem('myProduct');
		if (stored) {
			try { setCartData(JSON.parse(stored)); } catch (e) { localStorage.removeItem('myProduct'); }
		}
	}, []);

	const persist = (next) => {
		setCartData(next);
		if (next.length === 0) localStorage.removeItem('myProduct');
		else localStorage.setItem('myProduct', JSON.stringify(next));
	};

	const setQty = (index, delta) => {
		const next = cartData.map((item, i) => i === index
			? { ...item, quantity: Math.max(1, (parseInt(item.quantity, 10) || 1) + delta) }
			: item);
		persist(next);
	};

	const removeItem = (index) => persist(cartData.filter((_, i) => i !== index));

	const total = cartData.reduce((sum, item) => sum + unitPrice(item) * (parseInt(item.quantity, 10) || 1), 0);

	const api = (path, data) => axios.post(`${process.env.REACT_APP_APP_API_URL}${path}`, data, {
		headers: { Authorization: 'Bearer ' + mytoken },
	});

	const checkout = async (e) => {
		e.preventDefault();
		if (!mytoken) { toast.info('Log in to check out'); return; }
		if (!addressForm.name || !addressForm.phone || !addressForm.street || !addressForm.city) {
			toast.warn('Fill in your delivery details first');
			return;
		}
		if (payMethod === 'BRC' && !address) {
			const addr = await connect();
			if (!addr) { toast.warn(hasWallet ? 'Connect your wallet to pay in BRC' : 'Install BirrWallet or MetaMask to pay in BRC'); return; }
		}
		setPhase('placing');
		try {
			const products = cartData.map(({ productDetails, ...line }) => line);
			const res = await api('/api/order', { products, paymentMethod: payMethod, deliveryAddress: addressForm });
			if (!res.data.status) throw new Error(res.data.message || 'Could not place the order');
			const { orderGroupId, payment } = res.data;

			if (payMethod === 'COD') {
				persist([]);
				setReceipt({ orderGroupId, totalUSD: payment.totalUSD, method: 'COD' });
				setPhase('done');
				toast.success('Order placed — pay on delivery');
				return;
			}

			setPhase('paying');
			const txHash = await payBRC(payment.payTo, payment.amountBRC);

			setPhase('verifying');
			// the chain needs a moment to mine; retry verification briefly
			let verified = null;
			for (let attempt = 0; attempt < 10; attempt++) {
				const v = await api('/api/order/pay', { orderGroupId, txHash });
				if (v.data.status) { verified = v.data; break; }
				if (!/not yet mined|not found/i.test(v.data.message || '')) throw new Error(v.data.message);
				await new Promise(r => setTimeout(r, 2500));
			}
			if (!verified) throw new Error('Payment sent but not verified yet — it will appear in your orders once confirmed');

			persist([]);
			setReceipt({ orderGroupId, totalUSD: payment.totalUSD, amountBRC: payment.amountBRC, txHash, method: 'BRC' });
			setPhase('done');
			toast.success('Payment verified on-chain');
		} catch (err) {
			setPhase('cart');
			const msg = (err && err.message) || 'Checkout failed — try again';
			toast.error(/user (rejected|denied)/i.test(msg) ? 'Transaction cancelled in wallet' : msg);
		}
	};

	if (phase === 'done' && receipt) {
		return (
			<div className="bp-cart-page container">
				<div className="bp-checkout-success">
					<div className="bp-success-icon">✓</div>
					<h2>Order confirmed</h2>
					<p className="bp-dim">Order group <span className="bp-mono">{receipt.orderGroupId}</span></p>
					<div className="bp-receipt">
						<div><span>Total</span><span>${receipt.totalUSD}</span></div>
						{receipt.method === 'BRC' ? (
							<>
								<div><span>Paid on-chain</span><span className="bp-mono">{receipt.amountBRC} BRC</span></div>
								<div><span>Network</span><span>{CHAIN.name}</span></div>
								<div className="bp-receipt-tx">
									<span>Transaction</span>
									<a href={txUrl(receipt.txHash)} target="_blank" rel="noreferrer" className="bp-mono">
										{receipt.txHash.slice(0, 10)}…{receipt.txHash.slice(-8)} ↗
									</a>
								</div>
							</>
						) : (
							<div><span>Payment</span><span>Cash on delivery</span></div>
						)}
					</div>
					<div className="bp-success-actions">
						<Link to="/profile/pre-orders" className="bp-btn-primary">Track your order</Link>
						<Link to="/" className="bp-btn-ghost">Keep shopping</Link>
					</div>
				</div>
			</div>
		);
	}

	if (cartData.length === 0) {
		return (
			<div className="bp-cart-page container">
				<div className="bp-cart-empty">
					<div className="bp-empty-glyph">🛒</div>
					<h2>Your cart is empty</h2>
					<p className="bp-dim">Find something you like and it will show up here.</p>
					<Link to="/" className="bp-btn-primary">Browse the marketplace</Link>
				</div>
			</div>
		);
	}

	const busy = phase !== 'cart';

	return (
		<div className="bp-cart-page container">
			<h2 className="bp-cart-title">Your Cart</h2>
			<div className="bp-cart-grid">
				<div className="bp-cart-items">
					{cartData.map((item, index) => {
						const d = item.productDetails || {};
						const qty = parseInt(item.quantity, 10) || 1;
						return (
							<div className="bp-cart-item" key={index}>
								<img src={d.image || '/images/placeholder.svg'} alt={d.name} className="bp-cart-thumb" />
								<div className="bp-cart-item-info">
									<h4>{d.name}</h4>
									<p className="bp-dim">{d.description}</p>
									<span className="bp-mono bp-cart-unit">${unitPrice(item)} each</span>
								</div>
								<div className="bp-qty">
									<button onClick={() => setQty(index, -1)} disabled={busy || qty <= 1}>−</button>
									<span>{qty}</span>
									<button onClick={() => setQty(index, 1)} disabled={busy}>+</button>
								</div>
								<div className="bp-cart-line-total bp-mono">${(unitPrice(item) * qty).toFixed(2)}</div>
								<button className="bp-cart-remove" onClick={() => removeItem(index)} disabled={busy} aria-label="Remove">✕</button>
							</div>
						);
					})}
				</div>

				<div className="bp-checkout-panel">
					<h3>Checkout</h3>

					<div className="bp-field-row">
						<input placeholder="Full name" value={addressForm.name} disabled={busy}
							onChange={e => setAddressForm({ ...addressForm, name: e.target.value })} />
						<input placeholder="Phone" value={addressForm.phone} disabled={busy}
							onChange={e => setAddressForm({ ...addressForm, phone: e.target.value })} />
					</div>
					<input placeholder="Street address" value={addressForm.street} disabled={busy}
						onChange={e => setAddressForm({ ...addressForm, street: e.target.value })} />
					<input placeholder="City" value={addressForm.city} disabled={busy}
						onChange={e => setAddressForm({ ...addressForm, city: e.target.value })} />
					<input placeholder="Delivery notes (optional)" value={addressForm.notes} disabled={busy}
						onChange={e => setAddressForm({ ...addressForm, notes: e.target.value })} />

					<div className="bp-pay-methods">
						<button
							className={`bp-pay-method ${payMethod === 'BRC' ? 'active' : ''}`}
							onClick={() => setPayMethod('BRC')} disabled={busy}>
							<span className="bp-pay-method-name">⛓ Pay with BRC</span>
							<span className="bp-dim">Settled on {CHAIN.name}</span>
						</button>
						<button
							className={`bp-pay-method ${payMethod === 'COD' ? 'active' : ''}`}
							onClick={() => setPayMethod('COD')} disabled={busy}>
							<span className="bp-pay-method-name">💵 Cash on delivery</span>
							<span className="bp-dim">Pay when it arrives</span>
						</button>
					</div>

					<div className="bp-cart-summary">
						<div><span>Items</span><span>{cartData.length}</span></div>
						<div className="bp-cart-total"><span>Total</span><span className="bp-mono">${total.toFixed(2)}</span></div>
					</div>

					<button className="bp-btn-primary bp-checkout-btn" onClick={checkout} disabled={busy}>
						{phase === 'placing' ? 'Placing order…'
							: phase === 'paying' ? 'Confirm in wallet…'
							: phase === 'verifying' ? 'Verifying on-chain…'
							: payMethod === 'BRC' ? 'Pay & Place Order' : 'Place Order'}
					</button>
					{payMethod === 'BRC' && !address && (
						<p className="bp-dim bp-pay-hint">You'll be asked to connect your wallet.</p>
					)}
				</div>
			</div>
		</div>
	);
}
